// Version history for the LAX Connection Log app.
// Referenced by: ChangelogModal, HamburgerMenu, LoginScreen
export const CHANGELOG = [
  {
    version: "4.3.106",
    date: "Aug 2026",
    changes: [
      { type: "improved", text: "Shift export remarks now show as a full-width line below each connection (who/when tag + message) instead of being crammed into a narrow, all-caps table column." },
      { type: "improved", text: "Divider lines between connections in the shift export are darker and easier to follow." },
    ],
  },
  {
    version: "4.3.105",
    date: "Aug 2026",
    changes: [
      { type: "fixed", text: "Mission Control and flight route maps load correctly again — CARTO now requires an API key for basemap tiles." },
    ],
  },
  {
    version: "4.3.104",
    date: "Jun 2026",
    changes: [
      { type: "fixed", text: "Signing out from the menu now correctly deregisters push notifications and closes any open Mission Control windows." },
      { type: "fixed", text: "Urgent message haptic now only fires once per message, not on every 15-second poll while the banner is showing." },
      { type: "fixed", text: "Repeated sign-out calls during session expiry no longer cause duplicate side effects." },
      { type: "fixed", text: "APNs token save failure is now detected rather than silently ignored." },
      { type: "fixed", text: "TDT sync no longer affects connection cards belonging to other SCCOs." },
    ],
  },
  {
    version: "4.3.103",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Re-adding a connection card with the same flight numbers as a recently deleted card now works correctly." },
    ],
  },
  {
    version: "4.3.102",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Filter Connections sheet: swipe down to close, matching all other bottom sheets in the app." },
      { type: "improved", text: "Unhide Cards button moved to the top of the connection list (was at the bottom) and label shortened." },
      { type: "improved", text: "Filter Connections section labels are now larger and easier to read." },
    ],
  },
  {
    version: "4.3.101",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Hide card: cards are now fully invisible once hidden, and 'Unhide hidden cards' at the bottom of the list restores all at once. Hide card menu item is now white like the other menu options." },
      { type: "fixed", text: "Filter Connections sheet now uses the correct app fonts throughout." },
    ],
  },
  {
    version: "4.3.100",
    date: "May 2026",
    changes: [
      { type: "new", text: "SCCOs can now hide individual connection cards via the long-press menu. Hidden cards are invisible to other SCCOs and auto-clear at the end of the shift. A 'N hidden · Show' link at the bottom of the list lets you peek or restore them." },
    ],
  },
  {
    version: "4.3.99",
    date: "May 2026",
    changes: [
      { type: "improved", text: "SCCOs can now add a remark directly from the long-press / right-click menu on any connection card, without opening the full edit form." },
    ],
  },
  {
    version: "4.3.98",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Swipe down to close any modal on iPhone — a drag handle pill appears at the top of each sheet, and pulling it down 80px dismisses the modal." },
    ],
  },
  {
    version: "4.3.97",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Mission Control: estimated aircraft positions now use a three-phase speed profile (climb, cruise, descent), improving accuracy for flights near departure or on approach." },
    ],
  },
  {
    version: "4.3.96",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control now appears in the menu when using the app as a PWA on a Windows desktop (Edge or Chrome)." },
      { type: "fixed", text: "Mission Control now keeps overdue connections visible until they are marked completed or offloaded." },
      { type: "fixed", text: "Push notification toggle now shows a clear message if notifications are blocked in your browser, instead of silently doing nothing." },
      { type: "improved", text: "Mission Control: new toggle button (top-left) hides or shows the connections panel to give a clear view of the map." },
    ],
  },
  {
    version: "4.3.95",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Connection Map in shift report: city codes no longer overlap when multiple airports cluster in the same region." },
      { type: "improved", text: "Connection Map labels now have a subtle background halo so route lines don't obscure city codes." },
      { type: "improved", text: "LAX label moved below the center dot for cleaner map layout." },
    ],
  },
  {
    version: "4.3.94",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Face ID is now more reliable — enrollment persists across sign-outs and session expiries, and prompts automatically on app open." },
      { type: "fixed", text: "Push notifications restored after iOS native app work inadvertently changed the bundle ID and entitlements." },
    ],
  },
  {
    version: "4.3.93",
    date: "May 2026",
    changes: [
      { type: "improved", text: "PDF export reverted to browser print dialog — opens a clean print-ready view in a new tab." },
    ],
  },
  {
    version: "4.3.90",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Shift report Connection Map is now more compact." },
    ],
  },
  {
    version: "4.3.89",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Connection Map in shift report cleaned up — removed reference rings and compass labels for a cleaner look." },
    ],
  },
  {
    version: "4.3.88",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Shift report Connection Map redesigned as a radial (polar) layout — LAX at center, airports placed by direction with distance scaling, so nearby routes (SFO, SAN, PHX) are as readable as long-haul ones." },
      { type: "improved", text: "App icon badge now counts both unread messages and unopened new connection cards." },
      { type: "fixed", text: "Fixed Safari crash when app is left open for extended periods (bypassed Navigator Locks API which crashes in Safari 26.5 beta)." },
    ],
  },
  {
    version: "4.3.87",
    date: "May 2026",
    changes: [
      { type: "new", text: "Shift report now includes a Connection Map — an inline SVG world map showing all inbound/outbound routes color-coded by outcome (green = success, indigo = rebooked, blue = protected, red = unable)." },
      { type: "fixed", text: "App icon badge now clears immediately when leaving the app, not just on next open." },
    ],
  },
  {
    version: "4.3.86",
    date: "May 2026",
    changes: [
      { type: "new", text: "SCCO can now request passenger contact info (email/phone) when approving amenities — partner fills in a structured form and the contacts are sent back in the thread." },
      { type: "improved", text: "Amenity approval step now includes a 'Request passenger contact info' checkbox for seamless voucher delivery workflow." },
      { type: "fixed", text: "Removed pre-filled placeholder text from amenity request note field." },
    ],
  },
  {
    version: "4.3.85",
    date: "May 2026",
    changes: [
      { type: "new", text: "Partner airlines can now request hotel and/or meal amenities from United when the UA inbound flight is delayed — via the context menu or expanded card button." },
      { type: "new", text: "Amenity requests appear as structured messages in the thread; SCCO can approve or deny with a controllable/uncontrollable delay classification." },
      { type: "new", text: "Entry cards show a live amenity status badge (pending / approved / denied) once a request is submitted." },
    ],
  },
  {
    version: "4.3.84",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Message compose font size and family now match the sent message bubbles." },
      { type: "fixed", text: "Images attached to messages now display inline instead of as a filename link." },
      { type: "fixed", text: "Selecting the camera to attach a photo no longer force-quits the iOS app." },
    ],
  },
  {
    version: "4.3.83",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Cards with an ATD on the outbound flight now correctly show overdue (red) when the entry is still unresolved." },
      { type: "fixed", text: "Auto-sync no longer writes ATD as a raw string (e.g. 0735A) — it is now correctly formatted as HH:MM." },
    ],
  },
  {
    version: "4.3.82",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Push notifications now include passenger count and connection time when available." },
      { type: "improved", text: "Remark edit and delete icons are now more visible." },
    ],
  },
  {
    version: "4.3.81",
    date: "May 2026",
    changes: [
      { type: "improved", text: "UA SCCOs can now edit their own remarks inline — click the pencil icon next to any remark you submitted." },
      { type: "fixed", text: "Origin, gate, and flight number fields in the entry modal are wider to prevent text clipping with the SA font." },
    ],
  },
  {
    version: "4.3.80",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Remark input no longer jumps the cursor to the end when typing or inserting text mid-sentence." },
    ],
  },
  {
    version: "4.3.79",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now closes correctly when signing out from the main window." },
    ],
  },
  {
    version: "4.3.78",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now reliably closes when the main window signs out." },
    ],
  },
  {
    version: "4.3.77",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Mission Control popup now follows the main window's session — signing out in either window closes both. The popup no longer has its own independent timeout." },
    ],
  },
  {
    version: "4.3.76",
    date: "May 2026",
    changes: [
      { type: "improved", text: "Inactivity timeout extended from 30 to 45 minutes. Hard logout extended from 8 to 10 hours." },
    ],
  },
  {
    version: "4.3.75",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "App no longer freezes silently when the session expires — it now signs out and returns to the login screen automatically." },
    ],
  },
  {
    version: "4.3.74",
    date: "May 2026",
    changes: [
      { type: "fixed", text: "Login screen captcha widget removed — it was showing a broken white box since captcha is disabled in Supabase." },
    ],
  },
  {
    version: "4.3.73",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Post-shift (outlook) entries on the export report are more readable — opacity raised from 62% to 82%." },
    ],
  },
  {
    version: "4.3.72",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Message bubbles are larger and wider — text bumped to 15px with the SA font, bubbles widened from 78% to 88% of the thread width." },
    ],
  },
  {
    version: "4.3.71",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Mission Control map city codes are larger and easier to read — origin/destination labels bumped from 10px to 13px, LAX label from 11px to 14px." },
    ],
  },
  {
    version: "4.3.70",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Mission Control now uses the Star Alliance font (HelveticaNeueStd/Barlow) throughout — replacing IBM Plex Mono and plain Helvetica Neue for consistency with the rest of the app." },
    ],
  },
  {
    version: "4.3.69",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Context menu items use the primary SA font at 16px / medium weight — more legible and brand-consistent." },
    ],
  },
  {
    version: "4.3.68",
    date: "Apr 2026",
    changes: [
      { type: "fixed", text: "Expeditor sign header no longer overlaps the iOS status bar — uses the device safe area inset for correct spacing on all iPhone models." },
    ],
  },
  {
    version: "4.3.67",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign theme toggle is now smaller and requires a 1-second hold to activate, preventing accidental mode switches." },
    ],
  },
  {
    version: "4.3.66",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign light mode now uses the official Star Alliance Connection Service light header image." },
    ],
  },
  {
    version: "4.3.65",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Expeditor sign has a light/dark mode toggle in the lower-left corner — tap the sun or moon icon to switch. Sign defaults to dark mode." },
    ],
  },
  {
    version: "4.3.64",
    date: "Apr 2026",
    changes: [
      { type: "improved", text: "Connection cards on mobile now stack inbound and outbound vertically — full route info (gate, origin/dest, time) is always visible with no truncation. Tablet layout unchanged." },
    ],
  },
];
